// src/components/reviewerdash/ReviewerDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardCheck, 
  CheckCircle, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  Bell,
  Eye,
  Download,
  FileText,
  Calendar,
  User,
  Mail,
  AlertCircle,
  Check,
  Clock,
  Star,
  Paperclip,
  ArrowLeft,
  ArrowRight,
  Search,
  Filter,
  Grid,
  List,
  RefreshCw,
  Inbox,
  Clock3,
  AlertTriangle,
  Lock,
  Unlock,
  Play
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import RichTextViewer from '../common/RichTextViewer';
import { showToast } from '../../utils/toast';
import { handleDownloadFile } from '../../utils/utils.global';


// Enhanced parseQuillContent function to handle array format
const parseQuillContent = (content) => {
  if (!content) return '';

  try {
    // If it's already a string with HTML, return as is
    if (typeof content === 'string' && (content.includes('<p>') || content.includes('<div>'))) {
      return content;
    }

    // If it's an array of operations (like your email body format)
    if (Array.isArray(content)) {
      let html = '';
      content.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.color) {
            text = `<span style="color: ${op.attributes.color}">${text}</span>`;
          }
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      // Convert newlines to <br> tags for proper formatting
      html = html.replace(/\n/g, '<br>');

      return html;
    }

    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      return parseQuillContent(content.ops);
    }

    // If it's an object with message/compiledLetter (decision email body format)
    if (typeof content === 'object' && content !== null && (content.message || content.compiledLetter)) {
      return parseQuillContent(content.compiledLetter || content.message);
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return parseQuillContent(parsed);
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const getPriorityClass = () => {
    switch(priority?.toLowerCase()) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'normal':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'low':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getPriorityLabel = () => {
    switch(priority?.toLowerCase()) {
      case 'urgent':
        return 'Urgent';
      case 'high':
        return 'High Priority';
      case 'normal':
        return 'Normal Priority';
      case 'low':
        return 'Low Priority';
      default:
        return priority || 'Normal';
    }
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityClass()}`}>
      {getPriorityLabel()}
    </span>
  );
};

const ReviewerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State declarations
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('reviewer_dashboard_view');
    return saved || 'grid';
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  
  // Data states
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);


  const handleStart = (id) => {
    navigate(`/reviewerdash/review/${id}`);
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch invitations
  useEffect(() => {
    const fetchInvitations = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/reviewer/invitations', {
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: debouncedSearchTerm,
            status: filterStatus,
            priority: filterPriority !== 'all' ? filterPriority : undefined
          }
        });

        if (response.data.success) {
          const uniqueInvitations = Array.from(
            new Map(response.data.invitations.map(item => [item.id, item])).values()
          );
          
          setInvitations(uniqueInvitations);
          setTotalItems(response.data.total);
          setTotalPages(response.data.totalPages);
        } else {
          showToast(response.data.message || 'Failed to load invitations', 'error');
        }
      } catch (error) {
        console.error('Error fetching invitations:', error);
        showToast('Failed to load invitations. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitations();
  }, [currentPage, itemsPerPage, debouncedSearchTerm, filterStatus, filterPriority]);

  // Save view type to localStorage
  useEffect(() => {
    localStorage.setItem('reviewer_dashboard_view', viewType);
  }, [viewType]);

  // ... rest of your component code remains exactly the same

  // Calculate stats from actual data
  const stats = {
    total: totalItems,
    pending: invitations.filter(i => i.status === 'pending' || i.status === 'invite_sent').length,
    urgent: invitations.filter(i => i.priority?.toLowerCase() === 'urgent').length,
    high: invitations.filter(i => i.priority?.toLowerCase() === 'high').length,
    accepted: invitations.filter(i => i.status === 'accepted').length,
    completed: invitations.filter(i => i.status === 'completed').length,
    declined: invitations.filter(i => i.status === 'declined').length,
    dueThisWeek: invitations.filter(i => {
      if (!i.dueDate) return false;
      const dueDate = new Date(i.dueDate);
      const today = new Date();
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      return dueDate <= weekFromNow && dueDate >= today;
    }).length
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      invite_sent: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      accepted: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-purple-100 text-purple-800 border-purple-200',
      declined: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending':
      case 'invite_sent':
        return <Clock className="w-3 h-3 mr-1" />;
      case 'accepted':
        return <Check className="w-3 h-3 mr-1" />;
      case 'completed':
        return <CheckCircle className="w-3 h-3 mr-1" />;
      case 'declined':
        return <X className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      normal: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const getPriorityIcon = (priority) => {
    switch(priority?.toLowerCase()) {
      case 'urgent':
        return <AlertTriangle className="w-3 h-3 mr-1" />;
      case 'high':
        return <AlertCircle className="w-3 h-3 mr-1" />;
      case 'normal':
        return <Clock className="w-3 h-3 mr-1" />;
      case 'low':
        return <Clock3 className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  const handleViewInvitation = (invitation) => {
    setSelectedInvitation(invitation);
    setShowViewModal(true);
  };

  const handleAcceptClick = (invitation) => {
    setSelectedInvitation(invitation);
    setShowAcceptModal(true);
  };

  const handleDeclineClick = (invitation) => {
    setSelectedInvitation(invitation);
    setDeclineReason('');
    setShowDeclineModal(true);
  };

  const handleAcceptConfirm = async () => {
    setActionLoading(true);
    
    try {
      const response = await axios.post('/reviewer/invitations/accept', {
        invitationId: selectedInvitation.id,
        manuscriptId: selectedInvitation.manuscriptId
      });

      if (response.data.success) {
        // Update local state
        setInvitations(prev => prev.map(inv => 
          inv.id === selectedInvitation.id 
            ? { ...inv, status: 'accepted', canViewFiles: true } 
            : inv
        ));
        
        showToast('Invitation accepted successfully', 'success');
        setShowAcceptModal(false);
        setSelectedInvitation(null);
      } else {
        showToast(response.data.message || 'Failed to accept invitation', 'error');
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
      showToast(error.response?.data?.message || 'Failed to accept invitation. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeclineConfirm = async () => {
    setActionLoading(true);
    
    try {
      const response = await axios.post('/reviewer/invitations/decline', {
        invitationId: selectedInvitation.id,
        manuscriptId: selectedInvitation.manuscriptId,
        reason: declineReason
      });

      if (response.data.success) {
        // Update local state
        setInvitations(prev => prev.map(inv => 
          inv.id === selectedInvitation.id 
            ? { ...inv, status: 'declined' } 
            : inv
        ));
        
        showToast('Invitation declined', 'info');
        
        setShowDeclineModal(false);
        setSelectedInvitation(null);
        setDeclineReason('');
      } else {
        showToast(response.data.message || 'Failed to decline invitation', 'error');
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
      showToast(error.response?.data?.message || 'Failed to decline invitation. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterPriority('all');
    setFilterStatus('all');
    setCurrentPage(1);
  };

  const handleLogout = () => {
    localStorage.removeItem('reviewer_token');
    localStorage.removeItem('reviewer_email');
    navigate('/reviewerdash/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const closeSidebar = () => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  if (loading && invitations.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-purple-600 border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading review invitations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800">Review Invitations</h2>
          <p className="text-gray-600 mt-1">Manage your peer review invitations</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Invitations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Inbox className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Urgent</p>
                <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Due This Week</p>
                <p className="text-2xl font-bold text-orange-600">{stats.dueThisWeek}</p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock3 className="w-5 h-5 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Accepted</p>
                <p className="text-2xl font-bold text-green-600">{stats.accepted}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </div>
        </div>
        	
          {/* yOUTUBE SECTION  */}
					<h4 className="text-l font-bold text-gray-800">How to Submit Reviews</h4>
					<iframe width="560" height="315" src="https://www.youtube.com/embed/ivl-_VzTWQw?si=30EgMsNIJ0XBJGxm" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
					{/* End youtube section  */}

        {/* Search and Filters Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by title, ID, or author name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="completed">Completed</option>
                <option value="declined">Declined</option>
              </select>

              <select
                value={filterPriority}
                onChange={(e) => {
                  setFilterPriority(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Priorities</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>

              {/* View Toggle */}
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewType('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewType === 'grid' 
                      ? 'bg-white shadow-sm text-purple-600' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Grid View"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewType('table')}
                  className={`p-2 rounded-md transition-all ${
                    viewType === 'table' 
                      ? 'bg-white shadow-sm text-purple-600' 
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Table View"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                onClick={clearFilters}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Reset Filters"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Results Info */}
          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-gray-600">
              Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
              <span className="font-medium">{totalItems}</span> invitations
            </p>
            {(searchTerm || filterStatus !== 'all' || filterPriority !== 'all') && (
              <button
                onClick={clearFilters}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Invitations Display */}
        {invitations.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No invitations found</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' || filterPriority !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'You have no review invitations at this time.'}
            </p>
          </div>
        ) : viewType === 'table' ? (
          /* Table View */
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-purple-600">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Priority
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      ID
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Manuscript
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Corresponding Author
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Due Date
                    </th>
                    <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(invitation.status)}`}>
                          {getStatusIcon(invitation.status)}
                          {invitation.status === 'invite_sent' ? 'pending' : invitation.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PriorityBadge priority={invitation.priority} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {invitation.id}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{invitation.title}</div>
                        <div className="text-xs text-gray-500">{invitation.manuscriptId}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{invitation.correspondingAuthor}</div>
                        <div className="text-xs text-gray-500">{invitation.correspondingEmail}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invitation.dueDate ? new Date(invitation.dueDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleViewInvitation(invitation)}
                            className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          {(invitation.status === 'pending' || invitation.status === 'invite_sent') && (
                            <>
                              <button
                                onClick={() => handleAcceptClick(invitation)}
                                className="p-2 text-[--asfi-blue] hover:text-green-700 hover:bg-purple-50 rounded-lg transition-colors"
                                title="Accept"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeclineClick(invitation)}
                                className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                title="Decline"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          
                          {invitation.status === 'accepted' && (
                            <button
                              className="p-2 text-[--asfi-blue] hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Start Review"
                              onClick={() => handleStart(invitation.manuscriptId)}
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <PriorityBadge priority={invitation.priority} />
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(invitation.status)}`}>
                      {getStatusIcon(invitation.status)}
                      {invitation.status === 'invite_sent' ? 'pending' : invitation.status}
                    </span>
                  </div>

                  {/* Title and ID */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {invitation.title}
                  </h3>
                  <p className="text-sm text-purple-600 font-medium mb-3">
                    {invitation.manuscriptId}
                  </p>

                  {/* Authors */}
                  <div className="flex items-start mb-3">
                    <User className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {invitation.authors?.slice(0, 2).join(', ')}
                      {invitation.authors?.length > 2 && ' et al.'}
                    </p>
                  </div>

                  {/* Corresponding Author */}
                  <div className="flex items-start mb-3">
                    <Mail className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{invitation.correspondingAuthor}</p>
                      <p className="text-xs text-gray-500">{invitation.correspondingEmail}</p>
                    </div>
                  </div>

                  {/* File Access Indicator */}
                  {!invitation.canViewFiles && invitation.status === 'pending' && (
                    <div className="flex items-center mb-3 text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                      <Lock className="w-3 h-3 mr-1" />
                      Files locked until acceptance
                    </div>
                  )}

                  {invitation.canViewFiles && (
                    <div className="flex items-center mb-3 text-xs text-[--asfi-blue] bg-green-50 p-2 rounded-lg">
                      <Unlock className="w-3 h-3 mr-1" />
                      Files accessible
                    </div>
                  )}

                  {/* Due Date */}
                  <div className="flex items-center text-xs text-gray-500 mb-4">
                    <Calendar className="w-3 h-3 mr-1" />
                    Due: {invitation.dueDate ? new Date(invitation.dueDate).toLocaleDateString() : 'N/A'}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleViewInvitation(invitation)}
                      className="flex-1 px-3 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    
                    {(invitation.status === 'pending' || invitation.status === 'invite_sent') && (
                      <>
                        <button
                          onClick={() => handleAcceptClick(invitation)}
                          className="flex-1 px-3 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Accept
                        </button>
                        <button
                          onClick={() => handleDeclineClick(invitation)}
                          className="p-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                          title="Decline"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    
                    {invitation.status === 'accepted' && (
                      <button
                        className="flex-1 px-3 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        onClick={() => handleStart(invitation.manuscriptId)}
                      >
                        <FileText className="w-4 h-4" />
                        Start
                      </button>
                    )}

                    {invitation.status === 'review_saved'  && (
                      <button
                        className="flex-1 px-3 py-2 bg-[--asfi-blue] text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                        onClick={() => handleStart(invitation.manuscriptId)}
                      >
                        <Play className="w-3 h-3" />
                        Continue Review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-purple-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Outlet for nested routes */}
        {/* <Outlet /> */}
      </div>

      {/* View Invitation Modal */}
      {showViewModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Review Invitation Details</h3>
                    <p className="text-sm text-gray-500">View full invitation information</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedInvitation(null);
                  }}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Status and Priority */}
                <div className="flex flex-wrap gap-3">
                  <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedInvitation.status)}`}>
                    {getStatusIcon(selectedInvitation.status)}
                    {selectedInvitation.status === 'invite_sent' ? 'pending' : selectedInvitation.status}
                  </span>
                  <PriorityBadge priority={selectedInvitation.priority} />
                  
                  {/* File Access Indicator */}
                  {!selectedInvitation.canViewFiles && selectedInvitation.status === 'pending' && (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-amber-100 text-amber-800 border-amber-200">
                      <Lock className="w-4 h-4 mr-1" />
                      Files Locked
                    </span>
                  )}
                  
                  {selectedInvitation.canViewFiles && (
                    <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border bg-green-100 text-green-800 border-green-200">
                      <Unlock className="w-4 h-4 mr-1" />
                      Files Accessible
                    </span>
                  )}
                </div>

                {/* Title and ID */}
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">
                    {selectedInvitation.manuscriptId}
                  </p>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedInvitation.title}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">{selectedInvitation.type}</p>
                </div>

                {/* Invited By */}
                {selectedInvitation.invitedBy && (
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Invited By</h4>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{selectedInvitation.invitedBy}</p>
                        <p className="text-sm text-gray-600 flex items-center mt-1">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          {selectedInvitation.invitedByEmail}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Authors */}
                {selectedInvitation.authors && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Authors</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedInvitation.authors?.map((author, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {author}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Corresponding Author */}
                {selectedInvitation.correspondingAuthor && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Corresponding Author</h4>
                    <p className="text-sm text-gray-900">{selectedInvitation.correspondingAuthor}</p>
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Mail className="w-4 h-4 text-gray-400 mr-2" />
                      {selectedInvitation.correspondingEmail}
                    </p>
                  </div>
                )}

                {/* Abstract with Quill parsing */}
                {selectedInvitation.abstract && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Abstract</h4>
                    <div className="bg-gray-50 p-4 rounded-lg prose max-w-none">
                      {/* <div dangerouslySetInnerHTML={{ 
                        __html: parseQuillContent(selectedInvitation.abstract) || 'No abstract available' 
                      }} /> */}
                      <RichTextViewer content={selectedInvitation.abstract} />
                    </div>
                  </div>
                )}

                {/* Invitation Email Content */}
                {selectedInvitation.emailContent && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-purple-600" />
                      Invitation Email
                    </h4>
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">
                        From: {selectedInvitation.emailContent.sender}
                      </p>
                      <p className="text-xs text-gray-500">
                        Sent: {selectedInvitation.emailContent.sentAt ? new Date(selectedInvitation.emailContent.sentAt).toLocaleString() : 'N/A'}
                      </p>
                      <p className="text-sm font-medium text-gray-700 mt-2">
                        Subject: {selectedInvitation.emailContent.subject}
                      </p>
                      <div className="text-sm text-gray-600 bg-white p-3 rounded border border-purple-100">
                        <div dangerouslySetInnerHTML={{ 
                          __html: parseQuillContent(selectedInvitation.emailContent.body) 
                        }} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedInvitation.invitedDate && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Invited Date</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {new Date(selectedInvitation.invitedDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {selectedInvitation.dueDate && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-1">Due Date</h4>
                      <p className="text-sm text-gray-600 flex items-center">
                        <Clock className="w-4 h-4 text-gray-400 mr-2" />
                        {new Date(selectedInvitation.dueDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                </div>

                {/* Files - Only shown if user has accepted */}
         {selectedInvitation.files && Object.keys(selectedInvitation.files).length > 0 && selectedInvitation.canViewFiles && (
  <div>
    <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
      <Paperclip className="w-4 h-4 mr-2 text-gray-400" />
      Files
    </h4>
    <div className="space-y-2">
      {Object.entries(selectedInvitation.files).map(([key, value]) => (
        <div
          key={key}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
        >
          <div className="flex items-center">
            <FileText className="w-4 h-4 text-gray-400 mr-2" />
            <span className="text-sm text-gray-700 capitalize">
              {key.replace(/_/g, ' ')}:
            </span>
            <span className="ml-2 text-sm text-gray-600">{value}</span>
          </div>
          <button 
            className="p-1 text-gray-500 hover:text-purple-600 hover:bg-white rounded transition-colors" 
            onClick={() => handleDownloadFile(value, key)}
            title={`Download ${value}`}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  </div>
)}

                {/* Files Locked Message */}
                {!selectedInvitation.canViewFiles && selectedInvitation.status === 'pending' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <Lock className="w-5 h-5 text-amber-600 mr-2" />
                      <p className="text-sm text-amber-800">
                        Manuscript files are locked. You need to accept the invitation to view the files.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                
                {(selectedInvitation.status === 'pending' || selectedInvitation.status === 'invite_sent') && (
                  <>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleAcceptClick(selectedInvitation);
                      }}
                      className="px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Accept
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleDeclineClick(selectedInvitation);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <X className="w-4 h-4" />
                          Decline
                        </>
                      )}
                    </button>
                  </>
                )}

                {selectedInvitation.status === 'accepted' && (
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      handleStart(selectedInvitation.manuscriptId);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    Start Review
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Accept Confirmation Modal */}
      {showAcceptModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Accept Review Invitation</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to accept this review invitation?
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Manuscript:</p>
                <p className="text-sm text-gray-900 mt-1">{selectedInvitation.title}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedInvitation.manuscriptId}</p>
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> By accepting, you agree to provide a thorough review of this manuscript within the specified timeframe.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowAcceptModal(false)}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAcceptConfirm}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Accepting...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Confirm Accept
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decline Confirmation Modal */}
      {showDeclineModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Decline Review Invitation</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to decline this review invitation?
                  </p>
                </div>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-700">Manuscript:</p>
                <p className="text-sm text-gray-900 mt-1">{selectedInvitation.title}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedInvitation.manuscriptId}</p>
              </div>

              <div className="mb-4">
                <label htmlFor="declineReason" className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Declining (Optional)
                </label>
                <textarea
                  id="declineReason"
                  rows="3"
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Please provide a reason for declining..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800 flex items-start">
                  <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Note:</strong> The editor who invited you will be notified of your decision. Once declined, you cannot accept this invitation again.
                  </span>
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowDeclineModal(false)}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeclineConfirm}
                  disabled={actionLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Declining...
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4" />
                      Confirm Decline
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerDashboard;